import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { apiService } from '../../services/api';

interface AlterarSenhaObrigatoriaProps {
  userEmail: string;
  onComplete: () => void;
  onCancel: () => void;
}

const AlterarSenhaObrigatoria: React.FC<AlterarSenhaObrigatoriaProps> = ({ userEmail, onComplete, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    nova: false,
    confirmar: false
  });
  const [formData, setFormData] = useState({
    novaSenha: '',
    confirmarSenha: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Limpar erro ao digitar
  };

  const toggleShowPassword = (field: 'nova' | 'confirmar') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.novaSenha !== formData.confirmarSenha) {
      setError('Confirmação de senha não confere');
      return;
    }

    if (formData.novaSenha.length < 6) {
      setError('Nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiService.alterarSenhaPadrao(userEmail, formData.novaSenha, formData.confirmarSenha);
      onComplete();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao alterar senha. Tente novamente.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="d-flex justify-content-center align-items-center" 
      style={{
        height: '100vh', 
        backgroundColor: '#f8f9fa',
        margin: 0,
        padding: 0,
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999
      }}
    >
      <div 
        className="card shadow-lg" 
        style={{
          width: '400px', 
          padding: '0',
          margin: '0 auto',
          position: 'relative'
        }}
      >
        <div className="card-body p-4">
          <div className="text-center mb-4">
            <div className="text-warning mb-3">
              <AlertTriangle size={48} />
            </div>
            <h3 className="text-warning mb-2">Alteração de Senha Obrigatória</h3>
            <p className="text-muted">
              Por segurança, você precisa alterar sua senha padrão antes de continuar
            </p>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="nova-senha-page" className="form-label fw-medium">Nova Senha *</label>
              <div className="input-group">
                <input
                  id="nova-senha-page"
                  type={showPasswords.nova ? "text" : "password"}
                  className="form-control"
                  name="novaSenha"
                  value={formData.novaSenha}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => toggleShowPassword('nova')}
                  disabled={loading}
                >
                  {showPasswords.nova ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="form-text">
                Mínimo de 6 caracteres
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="confirmar-senha-page" className="form-label fw-medium">Confirmar Nova Senha *</label>
              <div className="input-group">
                <input
                  id="confirmar-senha-page"
                  type={showPasswords.confirmar ? "text" : "password"}
                  className="form-control"
                  name="confirmarSenha"
                  value={formData.confirmarSenha}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => toggleShowPassword('confirmar')}
                  disabled={loading}
                >
                  {showPasswords.confirmar ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <button 
                type="button" 
                className="btn btn-secondary btn-sm" 
                onClick={onCancel}
                disabled={loading}
                style={{width: '40%'}}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-warning btn-sm"
                disabled={loading}
                style={{width: '40%'}}
              >
                {loading ? (
                  <>
                    <output className="spinner-border spinner-border-sm me-2"></output>
                    Alterando...
                  </>
                ) : (
                  <>
                    <Lock size={14} className="me-2" />
                    Alterar
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AlterarSenhaObrigatoria;